import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';

import type { FormGroup, FormSchema } from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption, InputMessageSeverity } from '@modules/forms/types';
import { EAasmState } from '@modules/core/enums';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { usePermissionsStore } from '@modules/permissions/store';

import type { ITrackerTrafficSourceComposableOptions, ITrackerTrafficSourceForm } from '../types';
import {
  AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS,
  TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS,
} from '../constants';
import { useAffiliateTrackerTrafficSourcesQuery } from '../queries';

export interface ITrackerTrafficSourceComposable {
  formTrafficSourceSchema: ComputedRef<FormSchema<ITrackerTrafficSourceForm>>;
  formGroups: ComputedRef<FormGroup<ITrackerTrafficSourceForm>[]>;
  isCustomTrafficSource: ComputedRef<boolean>;
  changeTrafficSourcePermission: ComputedRef<boolean>;
  showTrafficSourceWarning: Ref<boolean>;
  onChange(key: keyof ITrackerTrafficSourceForm, value: ITrackerTrafficSourceForm[keyof ITrackerTrafficSourceForm]): void;
}

export default function useTrackerTrafficSource(
  options: Ref<ITrackerTrafficSourceComposableOptions>,
): ITrackerTrafficSourceComposable {
  const { getTranslation } = useI18n();
  const { hasPermission } = usePermissionsStore();

  const showTrafficSourceWarning = ref(false);

  const changeTrafficSourcePermission = computed<boolean>(() => hasPermission(AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS.CHANGE_TRAFFIC_SOURCE).value);

  const affId = computed(() => options.value.affiliateId);

  const enableQuery = computed(() => options.value.enableQuery && changeTrafficSourcePermission.value);

  const { data, isLoading } = useAffiliateTrackerTrafficSourcesQuery(affId, enableQuery);

  const trafficSourcesOptions = computed<IFormSelectInputOption<number>[]>(() => {
    return data.value?.list
      ?.filter(({ aasm_state }) => options.value.withoutDefault ? aasm_state === EAasmState.STANDARD : true)
      ?.map((item) => ({ value: item.id, label: item.name })) ?? [];
  });

  const isCustomTrafficSource = computed<boolean>(() => (
    options.value.trafficSourceValue != null
    && Array.isArray(data.value?.list)
    && data.value?.list.find((item) => options.value.trafficSourceValue === item.id)?.aasm_state !== EAasmState.DEFAULT
  ));

  const descriptionText = computed<string | undefined>(() => {
    if (isCustomTrafficSource.value) {
      return getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'CANNOT_CHANGE_TRAFFIC_SOURCE_INFO').value;
    }
    return showTrafficSourceWarning.value ? getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'SAVE_TRAFFIC_SOURCE_WARNING').value : undefined;
  });

  const trafficSourceWarningSeverity = computed<InputMessageSeverity>(() => isCustomTrafficSource.value ? 'info' : 'warn');

  const formTrafficSourceSchema = computed<FormSchema<ITrackerTrafficSourceForm>>(() => ({
    department: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'EDIT_TRAFFIC_SOURCE_DEPARTMENT').value,
      value: data.value?.department_name,
      readonly: true,
    },
    traffic_source_id: {
      type: EFormInputTypes.SELECT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'EDIT_TRAFFIC_SOURCE_TRAFFIC_SOURCE').value,
      options: trafficSourcesOptions.value,
      value: isCustomTrafficSource.value ? options.value.trafficSourceValue : undefined,
      required: true,
      readonly: isCustomTrafficSource.value,
      loading: isLoading.value,
      description: descriptionText.value,
      descriptionSeverity: trafficSourceWarningSeverity.value,
    },
  }));

  const formGroups = computed<FormGroup<ITrackerTrafficSourceForm>[]>(() => [
    {
      items: options.value.hideDepartment ? ['traffic_source_id'] : ['department', 'traffic_source_id'],
    },
  ]);

  function onChange(key: keyof ITrackerTrafficSourceForm, value: ITrackerTrafficSourceForm[keyof ITrackerTrafficSourceForm]): void {
    if (key === 'traffic_source_id' && !!value) {
      showTrafficSourceWarning.value = true;
    }
  }

  return {
    formTrafficSourceSchema,
    formGroups,
    isCustomTrafficSource,
    changeTrafficSourcePermission,
    showTrafficSourceWarning,
    onChange,
  };
}
